import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
import { Switch } from '@/components/ui/switch';
import {
  CheckCircle,
  XCircle,
  Clock,
  User,
  ExternalLink,
  Instagram,
  Youtube,
  Phone,
  Mail,
  MapPin,
  Calendar,
  CreditCard,
  AlertCircle,
  Ban,
} from 'lucide-react';
import { FaTiktok } from 'react-icons/fa';

export default function AdminApprovals() {
  const { session } = useAuth();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [rejectionNote, setRejectionNote] = useState('');
  const [processing, setProcessing] = useState(false);

  const fetchSubmissions = useCallback(async () => {
    if (!session?.access_token) return;
    
    setLoading(true);
    try {
      // Use backend API to get submissions with profiles (bypasses RLS)
      const response = await fetch(
        `${BACKEND_URL}/api/admin/submissions-with-profiles?status=${filterStatus}`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch submissions');
      }
      
      const data = await response.json();
      console.log('Submissions with profiles from API:', data);
      setSubmissions(data || []);
      
      // Auto-select first submission if available
      if (data && data.length > 0 && !selectedSubmission) {
        setSelectedSubmission(data[0]);
      }
    } catch (error) {
      console.error('Error fetching submissions:', error);
      toast.error('Göndermeler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, filterStatus]);

  useEffect(() => {
    if (session?.access_token) {
      fetchSubmissions();
    }
  }, [session?.access_token, filterStatus, fetchSubmissions]);

  const handleApprove = async () => {
    if (!selectedSubmission) return;
    setProcessing(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        toast.error('Oturum bulunamadı');
        return;
      }

      // Use backend API for approval (handles participant_number assignment)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${selectedSubmission.id}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'approved' })
      });

      if (!response.ok) {
        let errorMessage = 'Onaylama başarısız';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = `Sunucu hatası: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      toast.success('Görev onaylandı');
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      console.error('Error approving submission:', error);
      toast.error(error.message || 'Onaylama sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedSubmission) return;
    setProcessing(true);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData?.session?.access_token;
      
      if (!token) {
        toast.error('Oturum bulunamadı');
        return;
      }

      // Use backend API for rejection
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${selectedSubmission.id}/approve`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          status: 'rejected',
          rejection_note: rejectionNote || null
        })
      });

      if (!response.ok) {
        let errorMessage = 'Reddetme başarısız';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = `Sunucu hatası: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      toast.success('Görev reddedildi');
      setRejectionNote('');
      setSelectedSubmission(null);
      fetchSubmissions();
    } catch (error) {
      console.error('Error rejecting submission:', error);
      toast.error(error.message || 'Reddetme sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const handleVerifyField = async (field, currentValue) => {
    if (!selectedSubmission?.profiles) return;

    try {
      // Use backend API to verify field (bypasses RLS)
      const response = await fetch(
        `${BACKEND_URL}/api/admin/profiles/${selectedSubmission.profiles.id}/verify?field=${field}&verified=${!currentValue}`,
        {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to verify field');
      }

      toast.success(`${field} doğrulaması ${!currentValue ? 'yapıldı' : 'kaldırıldı'}`);
      
      const verificationField = `${field}_verified`;
      
      // Update local state
      setSelectedSubmission(prev => ({
        ...prev,
        profiles: {
          ...prev.profiles,
          [verificationField]: !currentValue
        }
      }));
      
      // Also update in submissions list
      setSubmissions(prev => prev.map(s => 
        s.id === selectedSubmission.id 
          ? { ...s, profiles: { ...s.profiles, [verificationField]: !currentValue } }
          : s
      ));
    } catch (error) {
      console.error('Error verifying field:', error);
      toast.error('Doğrulama sırasında hata oluştu');
    }
  };

  const handleBanUser = async () => {
    if (!selectedSubmission?.profiles) return;
    
    const userEmail = selectedSubmission.profiles.email;
    const userId = selectedSubmission.profiles.id;
    
    const banReason = window.prompt(
      `"${userEmail}" kullanıcısını engellemek istediğinizden emin misiniz? Açık oturumu kapanır ve tekrar giriş yapamaz.\n\nBan sebebi (ZORUNLU, kullanıcıya gösterilir):`,
      ''
    );
    if (banReason === null) {
      return;
    }
    if (banReason.trim().length < 5) {
      toast.error('Ban sebebi zorunlu (en az 5 karakter). Kullanıcı bu sebebi görür.');
      return;
    }
    
    setProcessing(true);
    try {
      // Use backend API to ban user (bypasses RLS trigger protection)
      const response = await fetch(`${BACKEND_URL}/api/admin/users/${userId}/ban`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({ is_banned: true, ban_reason: banReason.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to ban user');
      }

      toast.success(`${userEmail} engellendi`);
      fetchSubmissions();
    } catch (error) {
      console.error('Error banning user:', error);
      toast.error(error.message || 'Engelleme sırasında hata oluştu');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-brand-yellow text-brand-ink"><Clock className="w-3 h-3 mr-1" />Bekliyor</Badge>;
      case 'approved':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" />Onaylandı</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" />Reddedildi</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div data-testid="admin-approvals">
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-brand text-2xl text-brand-ink">Görev Onay Ekranı</h2>
        <div className="flex items-center gap-2">
          <Label className="text-sm">Durum:</Label>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-36" data-testid="filter-status-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="pending">Bekleyenler</SelectItem>
              <SelectItem value="approved">Onaylananlar</SelectItem>
              <SelectItem value="rejected">Reddedilenler</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-3 gap-4">
          <Skeleton className="h-96" />
          <Skeleton className="h-96 col-span-2" />
        </div>
      ) : submissions.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Henüz gönderim bulunmuyor</p>
        </div>
      ) : (
        <div className="grid grid-cols-3 gap-4">
          {/* Left Panel - Submissions List */}
          <Card className="h-[calc(100vh-220px)]">
            <CardHeader className="py-3">
              <CardTitle className="text-sm font-medium">Göndermeler ({submissions.length})</CardTitle>
            </CardHeader>
            <ScrollArea className="h-[calc(100%-60px)]">
              <div className="p-2 space-y-2">
                {submissions.map((submission) => (
                  <div
                    key={submission.id}
                    onClick={() => setSelectedSubmission(submission)}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedSubmission?.id === submission.id
                        ? 'border-brand-ink bg-brand-neon-soft'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid={`submission-item-${submission.id}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="font-medium text-sm truncate">
                        {submission.tasks?.title || 'Görev'}
                      </span>
                      {getStatusBadge(submission.status)}
                    </div>
                    <p className="text-xs text-gray-500 truncate">
                      {submission.profiles?.email || 'Kullanıcı'}
                    </p>
                    <div className="flex items-center justify-between">
                      <p className="text-xs text-gray-400">
                        {formatDate(submission.submitted_at)}
                      </p>
                      <ProofBadge status={submission.proof_check_status} />
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </Card>

          {/* Right Panel - Submission Details */}
          <Card className="col-span-2 h-[calc(100vh-220px)]">
            {selectedSubmission ? (
              <ScrollArea className="h-full">
                <div className="p-6">
                  {/* Submission Info */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="font-bold text-lg">{selectedSubmission.tasks?.title}</h3>
                        <p className="text-sm text-gray-500">
                          Kampanya: {selectedSubmission.tasks?.campaigns?.title || '-'}
                        </p>
                      </div>
                      {getStatusBadge(selectedSubmission.status)}
                    </div>

                    <div className="bg-gray-50 rounded-lg p-4 mb-4">
                      {selectedSubmission.submitted_link && (
                        <>
                          <Label className="text-sm font-medium">Gönderilen Link</Label>
                          <div className="flex items-center gap-2 mt-1">
                            <a href={selectedSubmission.submitted_link} target="_blank" rel="noopener noreferrer"
                              className="text-brand-ink font-semibold underline-offset-2 hover:underline flex items-center gap-1 text-sm" data-testid="submission-link">
                              {selectedSubmission.submitted_link}
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          </div>
                        </>
                      )}
                      {selectedSubmission.submitted_image_url && (
                        <div className="mt-2">
                          <Label className="text-sm font-medium">Yüklenen Görsel</Label>
                          <a href={selectedSubmission.submitted_image_url} target="_blank" rel="noopener noreferrer">
                            <img src={selectedSubmission.submitted_image_url} alt="Gönderim görseli" 
                              className="mt-1 max-h-40 rounded-lg border cursor-pointer hover:opacity-80" />
                          </a>
                        </div>
                      )}
                      {selectedSubmission.tracking_link && (
                        <div className="mt-2">
                          <Label className="text-xs font-medium text-gray-500">Tracking Link</Label>
                          <p className="text-xs font-mono text-brand-ink">{selectedSubmission.tracking_link}</p>
                        </div>
                      )}
                      <p className="text-xs text-gray-500 mt-2">
                        Gönderilme: {formatDate(selectedSubmission.submitted_at)}
                      </p>
                    </div>

                    <ProofCheckPanel submission={selectedSubmission} />

                    {selectedSubmission.status === 'pending' && (
                      <>
                        {/* Rejection Note */}
                        <div className="mb-4">
                          <Label htmlFor="rejection_note" className="text-sm">Red Notu (Opsiyonel)</Label>
                          <Textarea
                            id="rejection_note"
                            value={rejectionNote}
                            onChange={(e) => setRejectionNote(e.target.value)}
                            placeholder="Reddedilme sebebini yazın..."
                            className="mt-1"
                            rows={2}
                            data-testid="rejection-note-input"
                          />
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center gap-3">
                          <Button
                            onClick={handleApprove}
                            disabled={processing}
                            className="bg-green-600 hover:bg-green-700"
                            data-testid="approve-btn"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Onayla
                          </Button>
                          <Button
                            onClick={handleReject}
                            disabled={processing}
                            variant="destructive"
                            data-testid="reject-btn"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reddet
                          </Button>
                        </div>
                      </>
                    )}

                    {selectedSubmission.rejection_note && (
                      <div className="mt-4 p-3 bg-red-50 rounded-lg border border-red-200">
                        <div className="flex items-center gap-2 text-red-700 text-sm font-medium mb-1">
                          <AlertCircle className="w-4 h-4" />
                          Red Notu
                        </div>
                        <p className="text-sm text-red-600">{selectedSubmission.rejection_note}</p>
                      </div>
                    )}
                  </div>

                  <Separator className="my-6" />

                  {/* User Profile */}
                  {selectedSubmission.profiles ? (
                    <div>
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-bold text-lg flex items-center gap-2">
                          <User className="w-5 h-5" />
                          Kullanıcı Profili
                        </h4>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={handleBanUser}
                          disabled={processing}
                          data-testid="ban-user-btn"
                        >
                          <Ban className="w-4 h-4 mr-1" />
                          Kullanıcıyı Engelle
                        </Button>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        {/* Basic Info */}
                        <div className="space-y-3">
                          <ProfileField
                            icon={<Mail className="w-4 h-4" />}
                            label="E-posta"
                            value={selectedSubmission.profiles.email}
                          />
                          <ProfileField
                            icon={<User className="w-4 h-4" />}
                            label="Ad Soyad"
                            value={selectedSubmission.profiles.full_name}
                            verified={selectedSubmission.profiles.full_name_verified}
                            onVerify={() => handleVerifyField('full_name', selectedSubmission.profiles.full_name_verified)}
                          />
                          <ProfileField
                            icon={<Phone className="w-4 h-4" />}
                            label="Telefon"
                            value={selectedSubmission.profiles.phone}
                            verified={selectedSubmission.profiles.phone_verified}
                            onVerify={() => handleVerifyField('phone', selectedSubmission.profiles.phone_verified)}
                          />
                          <ProfileField
                            icon={<CreditCard className="w-4 h-4" />}
                            label="IBAN"
                            value={selectedSubmission.profiles.iban}
                            verified={selectedSubmission.profiles.iban_verified}
                            onVerify={() => handleVerifyField('iban', selectedSubmission.profiles.iban_verified)}
                          />
                          <ProfileField
                            icon={<Calendar className="w-4 h-4" />}
                            label="Doğum Tarihi"
                            value={selectedSubmission.profiles.birth_date}
                            verified={selectedSubmission.profiles.birth_date_verified}
                            onVerify={() => handleVerifyField('birth_date', selectedSubmission.profiles.birth_date_verified)}
                          />
                          <ProfileField
                            icon={<MapPin className="w-4 h-4" />}
                            label="Şehir"
                            value={selectedSubmission.profiles.city}
                            verified={selectedSubmission.profiles.city_verified}
                            onVerify={() => handleVerifyField('city', selectedSubmission.profiles.city_verified)}
                          />
                        </div>

                        {/* Social Media */}
                        <div className="space-y-3">
                          <ProfileField
                            icon={<Instagram className="w-4 h-4" />}
                            label="Instagram"
                            value={selectedSubmission.profiles.instagram_username ? `@${selectedSubmission.profiles.instagram_username} (${selectedSubmission.profiles.instagram_followers || 0} takipçi)` : null}
                            verified={selectedSubmission.profiles.instagram_username_verified}
                            onVerify={() => handleVerifyField('instagram_username', selectedSubmission.profiles.instagram_username_verified)}
                          />
                          {/* TikTok / YouTube are paused; shown only for legacy profiles that have them. */}
                          {selectedSubmission.profiles.tiktok_username && <ProfileField
                            icon={<FaTiktok className="w-4 h-4" />}
                            label="TikTok"
                            value={selectedSubmission.profiles.tiktok_username ? `@${selectedSubmission.profiles.tiktok_username} (${selectedSubmission.profiles.tiktok_followers || 0} takipçi)` : null}
                            verified={selectedSubmission.profiles.tiktok_username_verified}
                            onVerify={() => handleVerifyField('tiktok_username', selectedSubmission.profiles.tiktok_username_verified)}
                          />}
                          {selectedSubmission.profiles.youtube_username && <ProfileField
                            icon={<Youtube className="w-4 h-4" />}
                            label="YouTube"
                            value={selectedSubmission.profiles.youtube_username ? `@${selectedSubmission.profiles.youtube_username} (${selectedSubmission.profiles.youtube_followers || 0} abone)` : null}
                            verified={selectedSubmission.profiles.youtube_username_verified}
                            onVerify={() => handleVerifyField('youtube_username', selectedSubmission.profiles.youtube_username_verified)}
                          />}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                      <div className="flex items-center gap-2 text-yellow-700">
                        <AlertCircle className="w-4 h-4" />
                        <span className="font-medium">Kullanıcı profili bulunamadı</span>
                      </div>
                      <p className="text-sm text-yellow-600 mt-1">
                        User ID: {selectedSubmission.user_id}
                      </p>
                    </div>
                  )}
                </div>
              </ScrollArea>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                <div className="text-center">
                  <User className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Detayları görmek için bir gönderim seçin</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// Profile Field Component
function ProfileField({ icon, label, value, verified, onVerify }) {
  return (
    <div className="flex items-start justify-between gap-2 p-2 rounded-lg hover:bg-gray-50">
      <div className="flex items-start gap-2 min-w-0">
        <span className="text-gray-400 mt-0.5">{icon}</span>
        <div className="min-w-0">
          <div className="text-xs text-gray-500">{label}</div>
          <div className="text-sm font-medium truncate">
            {value || <span className="text-gray-400">Belirtilmemiş</span>}
          </div>
        </div>
      </div>
      {onVerify && value && (
        <div className="flex items-center gap-1">
          <span className="text-xs text-gray-500">{verified ? 'Doğrulandı' : 'Doğrula'}</span>
          <Switch
            checked={verified}
            onCheckedChange={onVerify}
            data-testid={`verify-${label.toLowerCase()}`}
          />
        </div>
      )}
    </div>
  );
}

const PROOF_BADGES = {
  passed: { label: 'Otomatik kontrol geçti', className: 'bg-green-100 text-green-800' },
  failed: { label: 'Kontrol başarısız', className: 'bg-red-100 text-red-800' },
  error: { label: 'Instagram kontrol edilemedi', className: 'bg-brand-yellow text-brand-ink' },
  skipped: { label: 'Otomatik kontrol kapalı', className: 'bg-gray-100 text-gray-700' },
};

function ProofBadge({ status }) {
  const badge = PROOF_BADGES[status];
  if (!badge) return null;
  return <Badge className={`${badge.className} text-[10px] px-1.5 py-0`}>{badge.label}</Badge>;
}

// Result of the Instagram scraper check done when the user submitted.
function ProofCheckPanel({ submission }) {
  const status = submission.proof_check_status;
  if (!status) return null;
  const check = submission.proof_check || {};
  const reasons = check.recheck_reasons || [];
  const number = (value) => (value == null ? '-' : Number(value).toLocaleString('tr-TR'));
  return (
    <div className="rounded-lg border p-4 mb-4">
      <div className="flex items-center justify-between mb-2">
        <Label className="text-sm font-medium">Instagram Kanıt Kontrolü</Label>
        <ProofBadge status={status} />
      </div>
      {status === 'error' && (
        <p className="text-sm text-brand-red-deep">Gönderim sırasında Instagram'a ulaşılamadı; lütfen linki elle kontrol edin.</p>
      )}
      {status === 'skipped' && (
        <p className="text-sm text-gray-600">Tarayıcı yapılandırılmadığı için otomatik kontrol yapılmadı.</p>
      )}
      {reasons.length > 0 && (
        <ul className="text-sm text-red-700 list-disc pl-5 mb-2">
          {reasons.map((reason) => <li key={reason}>{reason}</li>)}
        </ul>
      )}
      {check.owner_username && (
        <div className="grid grid-cols-3 gap-3 text-sm">
          <div><span className="text-gray-500">Hesap</span><div>@{check.owner_username}</div></div>
          <div><span className="text-gray-500">Paylaşım</span><div>{check.taken_at ? new Date(check.taken_at).toLocaleString('tr-TR') : '-'}</div></div>
          <div><span className="text-gray-500">Takipçi (gönderimde)</span><div>{number(check.followers)}</div></div>
          <div><span className="text-gray-500">Beğeni</span><div>{number(check.likes)}</div></div>
          <div><span className="text-gray-500">Yorum</span><div>{number(check.comments)}</div></div>
          <div><span className="text-gray-500">İzlenme</span><div>{number(check.views)}</div></div>
        </div>
      )}
      {submission.proof_checked_at && (
        <p className="text-xs text-gray-400 mt-2">Son kontrol: {new Date(submission.proof_checked_at).toLocaleString('tr-TR')}</p>
      )}
    </div>
  );
}
